import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Slider,
  Typography,
  Box,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const FilterModal = ({
  open,
  onClose,
  onApplyFilters,
  accommodations,
  filters,
  setFilters,
  initialStateFilters,
}) => {

  const { t } = useTranslation();

  // Extract unique values from accommodations data
  const getUniqueValues = (key) => {
    const values = accommodations.map((acc) => acc[key]).filter(Boolean);
    return [...new Set(values)];
  };

  const getUniqueValueForUniversities = () => {
    const allUniversities = accommodations
      .map((acc) => acc.propertyNearByUniversities || []) // her biri bir array
      .flat() // düzleştir
      .filter((uni) => uni && uni.university_title); // geçerli olanları al

    const uniqueNames = [
      ...new Set(allUniversities.map((uni) => uni.university_title)),
    ];

    return uniqueNames;
  };

  const roomTypes = ["Studio", "Ensuite", "Premium Studio", "Premium Ensuite"];
  const roomTypesTrsMap = ["studio", "ensuite", "premiumStudio", "premiumEnsuite"];
  const providers = getUniqueValues("provider");
  const residences = getUniqueValues("propertyName");
  const universities = getUniqueValueForUniversities();

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const handleArrayFilterChange = (filterType, value, checked) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: checked
        ? [...prev[filterType], value]
        : prev[filterType].filter((item) => item !== value),
    }));
  };

  const handleApplyFilters = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleClearFilters = () => {
    setFilters(initialStateFilters);
    onApplyFilters(initialStateFilters);
    onClose();
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.residence) count++;
    if (filters.provider) count++;
    if (filters.duration) count++;
    if (filters.university) count++;
    if (filters.checkIn) count++;
    if (filters.checkOut) count++;
    if (filters.roomType.length > 0) count++;
    if (filters.facilities.length > 0) count++;
    if (filters.roomFeatures.length > 0) count++;
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 100) count++;
    return count;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "90vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography textTransform={"capitalize"} variant="h6">{t("filterAccommodations")}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {getActiveFiltersCount() > 0 && (
            <Chip
              label={`${getActiveFiltersCount()} ${t("active")}`}
              color="primary"
              size="small"
            />
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Provider */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ textTransform: 'capitalize' }}>{t("provider")}</InputLabel>
              <Select
                value={filters.provider}
                label="Provider"
                onChange={(e) => handleFilterChange("provider", e.target.value)}
              >
                <MenuItem sx={{ textTransform: 'capitalize' }} value="">{t("allProviders")}</MenuItem>
                {providers.map((provider) => (
                  <MenuItem key={provider} value={provider}>
                    {provider}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Location & Residence */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ textTransform: 'capitalize' }}>{t("residence")}</InputLabel>
              <Select
                value={filters.residence}
                label="Residence"
                onChange={(e) =>
                  handleFilterChange("residence", e.target.value)
                }
              >
                <MenuItem sx={{ textTransform: 'capitalize' }} value="">{t("allResidences")}</MenuItem>
                {residences.map((residence) => (
                  <MenuItem key={residence} value={residence}>
                    {residence}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Price Range */}
          <Grid item xs={12}>
            <Typography textTransform={"capitalize"} gutterBottom>{t("dailyPriceRange")} (£)</Typography>
            <Slider
              value={filters.priceRange}
              onChange={(e, newValue) =>
                handleFilterChange("priceRange", newValue)
              }
              valueLabelDisplay="auto"
              min={0}
              max={100}
              marks={[
                { value: 0, label: "£0" },
                { value: 25, label: "£25" },
                { value: 50, label: "£50" },
                { value: 75, label: "£75" },
                { value: 100, label: "£100+" },
              ]}
            />
          </Grid>

          {/* Check-in Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("checkInDate")}
              type="date"
              value={filters.checkIn}
              onChange={(e) => handleFilterChange("checkIn", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Check-out Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("checkOutDate")}
              type="date"
              value={filters.checkOut}
              onChange={(e) => handleFilterChange("checkOut", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Duration */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t("stayDuration")}</InputLabel>
              <Select
                value={filters.duration}
                label="Stay Duration"
                onChange={(e) => handleFilterChange("duration", e.target.value)}
              >
                <MenuItem sx={{ textTransform: 'capitalize' }} value="">{t("anyDuration")}</MenuItem>
                <MenuItem sx={{ textTransform: 'capitalize' }} value="short">{t("shortStays")}</MenuItem>
                <MenuItem sx={{ textTransform: 'capitalize' }} value="medium">{t("mediumStays")}</MenuItem>
                <MenuItem sx={{ textTransform: 'capitalize' }} value="long">{t("longStays")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* University */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ textTransform: 'capitalize' }}>{t("nearByUniversity")}</InputLabel>
              <Select
                value={filters.university}
                label="Nearby University"
                onChange={(e) =>
                  handleFilterChange("university", e.target.value)
                }
              >
                <MenuItem sx={{ textTransform: 'capitalize' }} value="">{t("anyUniversity")}</MenuItem>
                {universities.map((uni) => (
                  <MenuItem key={uni} value={uni}>
                    {uni}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Room Type */}
          <Grid item xs={12}>
            <Typography textTransform={"capitalize"} variant="subtitle1" gutterBottom>
              {t("roomType")}
            </Typography>
            <FormGroup row>
              {roomTypes.map((type, idx) => (
                <FormControlLabel
                  key={type}
                  sx={{ textTransform: 'capitalize' }}
                  control={
                    <Checkbox
                      checked={filters.roomType.includes(type)}
                      onChange={(e) =>
                        handleArrayFilterChange(
                          "roomType",
                          type,
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={t(roomTypesTrsMap[idx])}
                />
              ))}
            </FormGroup>
          </Grid>

          {/* 
        //   Facilities
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>Facilities</Typography>
            <FormGroup row>
              {facilities.map(facility => (
                <FormControlLabel
                  key={facility}
                  control={
                    <Checkbox
                      checked={filters.facilities.includes(facility)}
                      onChange={(e) => handleArrayFilterChange('facilities', facility, e.target.checked)}
                    />
                  }
                  label={facility}
                />
              ))}
            </FormGroup>
          </Grid>

        //   Room Features
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>Room Features</Typography>
            <FormGroup row>
              {roomFeatures.map(feature => (
                <FormControlLabel
                  key={feature}
                  control={
                    <Checkbox
                      checked={filters.roomFeatures.includes(feature)}
                      onChange={(e) => handleArrayFilterChange('roomFeatures', feature, e.target.checked)}
                    />
                  }
                  label={feature}
                />
              ))}
            </FormGroup>
          </Grid>
           */}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button sx={{ textTransform: 'capitalize' }} onClick={handleClearFilters} variant="outlined">
          {t("clearAll")}
        </Button>
        <Button sx={{ textTransform: 'capitalize' }} onClick={onClose} variant="outlined">
          {t("cancel")}
        </Button>
        <Button sx={{ textTransform: 'capitalize' }} onClick={handleApplyFilters} variant="contained">
          {t("applyFilters")} ({getActiveFiltersCount()})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterModal;
